export const CHAT_ALLOWED_EXTENSIONS = [
    '.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.tiff',
    '.pdf', '.zip', '.rar',
    '.docx', '.xlsx', '.pptx',
    '.txt', '.md', '.csv', '.json', '.xml', '.yaml', '.yml', '.html', '.htm', '.log',
    '.js', '.ts', '.jsx', '.tsx', '.py', '.cs', '.java', '.go',
    '.rb', '.php', '.swift', '.css', '.sh', '.sql',
    '.mp4', '.avi', '.mov', '.mp3',
] as const

export const CHAT_LEGACY_OFFICE_EXTENSIONS = ['.doc', '.xls', '.ppt'] as const

const CHAT_LEGACY_OFFICE_EXTENSIONS_SET = new Set<string>(CHAT_LEGACY_OFFICE_EXTENSIONS)

const RENDERABLE_IMAGE_TYPES = new Set([
    'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/bmp',
])

export function isRenderableImage(contentType: string): boolean {
    return RENDERABLE_IMAGE_TYPES.has(contentType.toLowerCase())
}

export const CHAT_ALLOWED_EXTENSIONS_SET = new Set<string>(CHAT_ALLOWED_EXTENSIONS)
export const CHAT_MAX_FILE_SIZE = 20 * 1024 * 1024
export const CHAT_MAX_ATTACHMENTS = 5

export const MAX_FILE_SIZE_BYTES = 60 * 1024 * 1024
export const MAX_ATTACHMENTS = 100
export const MAX_BATCH_SIZE_BYTES = 250 * 1024 * 1024
export const CONTEXT_MAX_BATCH_SIZE_BYTES = 1024 * 1024 * 1024

export const ALLOWED_EXTENSIONS = new Set([
    '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tiff',
    '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt',
    '.zip', '.rar',
    '.xml', '.json', '.csv', '.log', '.md', '.htm', '.html',
    '.mp4', '.avi', '.mov', '.mp3',
])

export const ACCEPTED_TYPES = [
    'image/png', 'image/jpeg', 'image/gif', 'image/bmp', 'image/tiff',
    'application/pdf', 'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain', 'application/zip', 'application/x-rar-compressed',
    'application/xml', 'text/xml', 'application/json', 'text/csv',
    'text/markdown', 'text/html',
    'video/mp4', 'video/x-msvideo', 'video/quicktime', 'audio/mpeg',
].join(',') + ',.log,.md,.htm,.html'

export function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes}B`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
}

export function getFileExtension(fileName: string): string {
    return '.' + (fileName.split('.').pop()?.toLowerCase() ?? '')
}

export interface ChatFileValidation {
    valid: File[]
    errors: string[]
}

export function validateChatFiles(files: File[], currentCount: number): ChatFileValidation {
    const valid: File[] = []
    const errors: string[] = []

    for (const file of files) {
        if (currentCount + valid.length >= CHAT_MAX_ATTACHMENTS) {
            errors.push(`Maximum ${CHAT_MAX_ATTACHMENTS} files per message.`)
            break
        }
        const ext = getFileExtension(file.name)
        if (CHAT_LEGACY_OFFICE_EXTENSIONS_SET.has(ext)) {
            errors.push(`"${file.name}": legacy Office format is not supported; save it as .docx, .xlsx or .pptx.`)
            continue
        }
        if (!CHAT_ALLOWED_EXTENSIONS_SET.has(ext)) {
            errors.push(`"${file.name}": file type not allowed.`)
            continue
        }
        if (file.size > CHAT_MAX_FILE_SIZE) {
            errors.push(`"${file.name}": file exceeds 20MB limit.`)
            continue
        }
        valid.push(file)
    }

    return { valid, errors }
}

export function limitBatchSize(
    files: File[],
    maxBatchBytes: number,
    alreadyQueuedBytes = 0,
): { accepted: File[]; errors: string[] } {
    const accepted: File[] = []
    const errors: string[] = []
    let total = alreadyQueuedBytes
    for (const file of files) {
        if (total + file.size > maxBatchBytes) {
            errors.push(`"${file.name}" could not be added: files uploaded together must not exceed ${maxBatchBytes / (1024 * 1024)} MB in total.`)
        } else {
            total += file.size
            accepted.push(file)
        }
    }
    return { accepted, errors }
}

export function validateAttachmentFiles(files: File[], currentCount: number, queuedBytes = 0): { valid: File[]; errors: string[] } {
    const errors: string[] = []
    const accepted: File[] = []
    for (const file of files) {
        if (!ALLOWED_EXTENSIONS.has(getFileExtension(file.name))) {
            errors.push(`"${file.name}" has an unsupported file type.`)
        } else if (/[\\/]|\.\./.test(file.name)) {
            errors.push(`"${file.name}" must not contain path separators or "..".`)
        } else if (file.size > MAX_FILE_SIZE_BYTES) {
            errors.push(`"${file.name}" exceeds the ${MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB size limit.`)
        } else {
            accepted.push(file)
        }
    }

    const slotsRemaining = Math.max(MAX_ATTACHMENTS - currentCount, 0)
    accepted.slice(slotsRemaining).forEach(file =>
        errors.push(`"${file.name}" could not be uploaded: attachment limit of ${MAX_ATTACHMENTS} reached.`)
    )

    const batch = limitBatchSize(accepted.slice(0, slotsRemaining), MAX_BATCH_SIZE_BYTES, queuedBytes)

    return { valid: batch.accepted, errors: [...errors, ...batch.errors] }
}

const PASTED_IMAGE_EXTENSIONS: Record<string, string> = {
    'image/png': '.png',
    'image/jpeg': '.jpg',
    'image/gif': '.gif',
    'image/webp': '.webp',
    'image/bmp': '.bmp',
    'image/tiff': '.tiff',
}

// Browsers name every pasted screenshot "image.png", so those names carry no information.
const GENERIC_CLIPBOARD_NAMES = new Set(['image', 'screenshot'])

export function namePastedImage(file: File, sequence: number, takenNames: Iterable<string> = []): File {
    const mimeType = file.type.toLowerCase()
    const subtype = mimeType.split('/')[1]?.split('+')[0] ?? ''
    const extension = PASTED_IMAGE_EXTENSIONS[mimeType] ?? `.${subtype}`
    const baseName = file.name.replace(/\.[^.]*$/, '').trim()
    const isGeneric = !baseName || GENERIC_CLIPBOARD_NAMES.has(baseName.toLowerCase())

    const taken = new Set(Array.from(takenNames, (name) => name.toLowerCase()))
    const preferred = isGeneric ? `pasted-image-${sequence}` : baseName
    let candidate = `${preferred}${extension}`
    for (let copy = 2; taken.has(candidate.toLowerCase()); copy++) {
        candidate = `${preferred} (${copy})${extension}`
    }

    return new File([file], candidate, { type: file.type })
}
